import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import type { ValuationSummary } from '../../../models/valuation.models';
import { AuthService } from '../../../services/auth.service';
import { ValuationService } from '../../../services/valuation.service';

@Component({
  selector: 'app-valuations-page',
  imports: [DatePipe],
  templateUrl: './valuations-page.component.html',
  styleUrl: './valuations-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ValuationsPageComponent {
  private readonly authService = inject(AuthService);
  private readonly valuationService = inject(ValuationService);
  private readonly router = inject(Router);

  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  private readonly hasLoaded = signal(false);
  private readonly currentUserId = signal<string | null>(null);
  private readonly valuations = signal<ValuationSummary[]>([]);

  protected readonly myValuations = computed(() =>
    this.valuations().filter((v) => v.user_id !== null && v.user_id === this.currentUserId()),
  );
  protected readonly otherValuations = computed(() =>
    this.valuations().filter((v) => v.user_id === null || v.user_id !== this.currentUserId()),
  );

  constructor() {
    effect(() => {
      if (this.hasLoaded()) {
        return;
      }
      void this.loadValuations();
    });
  }

  protected openValuation(sessionId: string): void {
    void this.router.navigate(['/valuations', sessionId]);
  }

  protected startNewValuation(): void {
    void this.router.navigate(['/valuations', 'new']);
  }

  private async loadValuations(): Promise<void> {
    this.hasLoaded.set(true);
    this.loading.set(true);
    this.error.set(null);

    try {
      const [me, list] = await Promise.all([
        firstValueFrom(this.authService.getCurrentUser()).catch(() => null),
        firstValueFrom(this.valuationService.listValuations()),
      ]);
      this.currentUserId.set(me?.user.id ?? null);
      const sorted = [...list.valuations].sort((a, b) =>
        b.updated_at.localeCompare(a.updated_at),
      );
      this.valuations.set(sorted);
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Unable to load valuations.');
    } finally {
      this.loading.set(false);
    }
  }
}
